import { Link } from 'react-router-dom';
import type { Ended } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { Check } from './check';

/**
 * The choice to end what acts for the person beyond their sessions, their AI apps and access tokens
 * (D196), offered wherever sessions end because the account may be in someone else's hands: ticked by
 * default only in a recovery, unticked when signing out of other devices, everywhere or changing the
 * password. `linked` adds the way to both lists, for a person who is signed in and may review them first.
 */
export function ActingChoice({ checked, onChange, linked, disabled }: { checked: boolean; onChange: (checked: boolean) => void; linked?: boolean; disabled?: boolean }) {
	const { text } = usePresence();
	const note = linked ? (
		<>
			{text('acting.note')} <Review />
		</>
	) : (
		text('acting.note')
	);
	return <Check label={text('acting.end')} checked={checked} onChange={onChange} disabled={disabled} note={note} />;
}

/** "See them first in AI apps with access and Access tokens", each a link to its list */
function Review() {
	const { text } = usePresence();
	const [before, middle, after] = text('acting.review', { apps: '\u0000', tokens: '\u0000' }).split('\u0000');
	return (
		<>
			{before}
			<Link className="underline" to="/connections">
				{text('nav.connections')}
			</Link>
			{middle}
			<Link className="underline" to="/tokens">
				{text('nav.tokens')}
			</Link>
			{after}
		</>
	);
}

/** What a choice to end what acts for the person ended, in words: "Access removed: 2 AI apps and one access token." */
export function removed(text: (key: string, values?: Record<string, string | number>) => string, access: Ended): string {
	const parts = [access.apps ? text('acting.apps', { count: access.apps }) : null, access.tokens ? text('acting.tokens', { count: access.tokens }) : null].filter(Boolean);
	return parts.length ? text('acting.ended', { what: parts.join(text('acting.and')) }) : text('acting.none');
}
